/* eslint-disable complexity */
import React, { useContext, useMemo, useState, memo } from 'react';
import { NodeViewWrapper, NodeViewContent } from '@tiptap/react';
import { Icon, Spinner } from '@scale/scaleui';
import { CodeBlockCopyButton } from '../../../../coding/shared/CodeBlockCopyButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CodeBlockExecutionOutput from '../../../../coding/CodeBlockExecutionOutput';
import { CodingScratchpad } from '../../../../coding/CodingScratchpad';
import { useCodeBlockStore } from '../../../../hooks/useCodeBlockStore';
import useCodeExecutionState from '../../../../coding/hooks/useCodeExecutionState';
import cn from 'classnames';
import { twMerge } from 'tailwind-merge';
import { ChatTaskContext } from '../../../../contexts/ChatTaskContext';
import crypto from 'crypto';
import {
  LANGUAGE_OPTIONS as SUPPORTED_EXECUTION_LANGUAGES,
  fullLanguageMap,
} from '@scale/shared/sphereEngine/types';
import { Node as PMNode } from '@tiptap/pm/model';
import { Editor as TipTapEditor } from '@tiptap/core';
import { getContentSerializer } from '../Content';
import { extractCodeBlocks } from '../../../../coding/helpers';
import { CodeBlockPreviewNodeT } from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor/plugins/coding/CodeBlockPreview';

// Function to convert language identifier to a readable format
const getReadableLanguage = (language: string) => {
  return fullLanguageMap[language] || language.toUpperCase();
};

const NUM_LINES_COLLAPSED = 11;

export default memo(function CodeBlockPreviewWithExecutionNodeView({
  editor: proseMirrorEditor,
  node: proseMirrorNode,
  extension,
}: {
  selected: boolean;
  editor: TipTapEditor;
  node: PMNode;
  extension: CodeBlockPreviewNodeT;
  getPos: () => number;
  updateAttributes: (attrs: Record<string, any>) => void;
}) {
  const [loading] = useState(false);
  const code: string = (proseMirrorNode as any)?.content?.content?.[0]?.text ?? '';
  const languageOverride: string = proseMirrorNode?.attrs?.language ?? 'plaintext';
  const serialize = getContentSerializer(proseMirrorEditor);

  const markdown = serialize();

  const codeBlocks = extractCodeBlocks(markdown);
  const codeBlockIndex = codeBlocks.findIndex(
    codeBlock => codeBlock.code === String(code).replace(/\n$/, ''),
  );

  const [isExpanded, setIsExpanded] = useState(extension.options.defaultExpanded);
  const language = languageOverride ?? '';
  const taskContext = useContext(ChatTaskContext);
  const customCodeEnvironments = taskContext?.codingConfig?.customCodeEnvironments;

  // We only allow code execution if there is exactly one custom code environment or if there's
  // no custom code environment AND the language is supported. If multiple custom code environments
  // exist, we don't allow code execution - contributors must use coding scratchpad to execute code instead.
  const canExecuteCode =
    customCodeEnvironments?.length === 1 ||
    ((!customCodeEnvironments || customCodeEnvironments.length === 0) &&
      !!SUPPORTED_EXECUTION_LANGUAGES.find(({ id }) => id === language));

  // Hashing is an expensive computation that we don't want to perform every time
  // `code` changes as it gets streamed in fast via MRS.
  const storeKey = useMemo(() => {
    if (loading) {
      return '';
    }

    return crypto
      .createHash('sha256')
      .update(`${code}-${language}-executableCodeBlock`)
      .digest('hex');
  }, [code, language, loading]);

  const { getScratchpadOpenState, setScratchpadOpenState } = useCodeBlockStore();
  const [scratchpadOpen, setScratchpadOpen] = [
    getScratchpadOpenState(storeKey),
    (open: boolean) => setScratchpadOpenState(storeKey, open),
  ];

  const {
    outputTabOpen,
    outputValue,
    tab,
    isFetching,
    fetchError,
    loadingTaskIdForLogging,
    handleExecuteCode,
    setOutputTabOpen,
    setTab,
  } = useCodeExecutionState({
    storeKey,
    code,
    language,
    customCodeEnvironment: customCodeEnvironments?.[0],
  });

  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const numLines = code.split(/\r\n|\r|\n/).length;

  return (
    <NodeViewWrapper className="hljs rounded-md">
      <>
        <div
          className={twMerge(
            'group bg-transparent relative px-0 py-0 scale-theme',
            isExpanded ? 'overflow-auto' : 'overflow-hidden',
          )}
        >
          <div className="text-white text-xs font-medium font-mono px-3 flex items-center justify-between bg-neutral-800 rounded-t-md py-1.5">
            <span className="text-neutral-0">{getReadableLanguage(language)}</span>
            <div className="flex items-center justify-end">
              {!loading && canExecuteCode && (
                <div className="pr-1">
                  {isFetching ? (
                    <Spinner className="pr-2" size="xs" variant="primary" />
                  ) : (
                    <Icon
                      icon={['far', 'play']}
                      className="hover:bg-neutral-700/40"
                      variant="white"
                      onClick={e => {
                        e.stopPropagation();
                        handleExecuteCode();
                      }}
                      disabled={isFetching || loadingTaskIdForLogging}
                      tooltip="Run Code"
                      tooltipProps={{ side: 'top', delayDuration: 100 }}
                      data-testid="run-code-button"
                    />
                  )}
                </div>
              )}
              {!loading && customCodeEnvironments && customCodeEnvironments?.length > 1 && (
                <Icon
                  icon={['far', 'exclamation-circle']}
                  className="hover:bg-neutral-800"
                  variant="white"
                  tooltip="Please use the coding scratchpad (click on the pencil icon to the right) to execute and test your code."
                  tooltipProps={{ side: 'top', delayDuration: 100 }}
                />
              )}
              {!loading && (
                <div className="pr-1">
                  <Icon
                    icon={['far', 'pen']}
                    className="hover:bg-neutral-700/40"
                    variant="white"
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();
                      setScratchpadOpen(true);
                    }}
                    tooltip="Scratchpad (changes made are not saved)"
                    tooltipProps={{ side: 'top', delayDuration: 100 }}
                  />
                </div>
              )}
              <div className="pl-2 pr-3 pt-1">
                <CodeBlockCopyButton code={code} />
              </div>
              {numLines > NUM_LINES_COLLAPSED && isExpanded && (
                <button
                  onClick={e => toggleExpand(e)}
                  className="border ml-1 rounded-md py-1.5 px-2 text-xs flex items-center justify-center bg-neutral-600 border-neutral-500 text-neutral-0"
                >
                  Collapse
                  <FontAwesomeIcon icon="chevron-up" className="ml-1" />
                </button>
              )}
            </div>
          </div>
          <div>
            <div
              className={cn(
                'p-3 bg-neutral-900',
                isExpanded
                  ? 'max-h-[900px] overflow-auto rounded-b-md'
                  : 'max-h-[290px] overflow-hidden',
              )}
            >
              <NodeViewContent className="not-wysiwyg font-mono" />
            </div>
            {numLines > NUM_LINES_COLLAPSED && !isExpanded && (
              <div className="flex items-center justify-center py-2 text-white text-xs font-medium font-mono bg-neutral-900 rounded-b-md">
                <button
                  onClick={e => toggleExpand(e)}
                  className="border ml-1 text-white rounded-md py-1.5 px-2 text-xs flex items-center justify-center bg-neutral-600 border-neutral-500 text-neutral-0"
                >
                  Expand
                  <FontAwesomeIcon icon="chevron-down" className="ml-1" />
                </button>
              </div>
            )}
          </div>
          {language !== 'text' && outputTabOpen && (
            <div className="border-none pt-4" onClick={e => e.stopPropagation()}>
              <CodeBlockExecutionOutput
                className="w-full h-40 p-0 whitespace-pre-line"
                outputValue={outputValue ?? ''}
                setOutputTabOpen={setOutputTabOpen}
                tab={tab}
                setTab={setTab}
                fetchError={fetchError as boolean}
                fullScreenEnabled={false}
                fullWidthEnabled={true}
              />
            </div>
          )}
          {scratchpadOpen && (
            <div className="fixed top-0 left-0 w-full h-full bg-utility-black/50 flex flex-col justify-center items-center z-[100]">
              <CodingScratchpad
                rawCode={code}
                originalLanguage={language}
                codeBlocksInMarkdownText={codeBlocks}
                setOpen={setScratchpadOpen}
                codeBlockIndex={codeBlockIndex}
                customCodeEnvironments={customCodeEnvironments}
              />
            </div>
          )}
        </div>
      </>
    </NodeViewWrapper>
  );
});
